"use client";

import FeedbackRating from "@/components/common/feedback/feedback-rating";
import { FEEDBACK_API, STATS_API } from "@/lib/constants";
import { useAuth } from "@/providers/auth-provider";
import { useQuery } from "@tanstack/react-query";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";

async function getStatsData() {
	try {
		const url = new URL(STATS_API);
		url.searchParams.set("query", "usersCount");

		const res = await fetch(url, { next: { revalidate: 600 } });
		if (!res.ok) {
			const { message } = await res.json();
			throw new Error(message);
		}

		const data = (await res.json()) as { usersCount: number };

		return data;
	} catch (error) {
		return {
			usersCount: 0,
		};
	}
}

export const CreateAccountCard = () => {
	const { isLoggedIn } = useAuth();
	const [enableSubmittedFeedback, setEnableSubmittedFeedback] = useState<boolean>(true);
	const { isLoading, data = { usersCount: 0 } } = useQuery({
		queryKey: ["usersCount"],
		queryFn: getStatsData,
		refetchOnWindowFocus: false,
	});

	// Check if a user has already submitted a feedback
	const getUserFeedback = useCallback(async () => {
		try {
			const url = new URL(FEEDBACK_API);
			const response = await fetch(url);
			if (response.ok) {
				setEnableSubmittedFeedback(false);
			} else {
				setEnableSubmittedFeedback(true);
			}
		} catch (error) {
			console.log(error);
		}
	}, []);

	useEffect(() => {
		if (isLoggedIn) {
			getUserFeedback();
		}
	}, [isLoggedIn, getUserFeedback]);

	return (
		<div className="relative rounded-lg bg-white p-6">
			<h6 className="mb-2 text-xl font-semibold">
				Find buyers, sellers, investors and service providers in ProptechBuzz!
			</h6>

			<div className="mt-6 flex items-center gap-1">
				{isLoading ? (
					<Skeleton height={12} count={1} className="mb-4 block" />
				) : (
					<div className="flex items-center justify-start gap-2">
						<div className="flex items-center -space-x-4">
							<Image
								src="/images/person-1.jpg"
								alt="person image"
								width={100}
								height={100}
								className="h-9 w-9 min-w-[36px] rounded-full border-2 border-white object-cover"
							/>
							<Image
								src="/images/person-2.jpg"
								alt="person image"
								width={100}
								height={100}
								className="h-9 w-9 min-w-[36px] rounded-full border-2 border-white object-cover"
							/>
							<Image
								src="/images/person-3.jpg"
								alt="person image"
								width={100}
								height={100}
								className="h-9 w-9 min-w-[36px] rounded-full border-2 border-white object-cover"
							/>
						</div>
						<p className="text-left text-lg font-semibold text-gray-800">
							<span className="text-brand-primary">{data.usersCount}+ others</span> joined till now
						</p>
					</div>
				)}
			</div>

			{isLoggedIn && !enableSubmittedFeedback ? (
				<div className="mt-2 flex flex-col">
					<p className="text-sm text-gray-600">
						Be part of the buzz - share your precious feedback by rating our experience.
					</p>
					<div className="mt-4">
						<FeedbackRating setHasSubmittedFeedback={setEnableSubmittedFeedback} />
					</div>
				</div>
			) : null}

			{!isLoggedIn ? (
				<Link
					className="mt-6 block w-full rounded-md bg-brand-primary p-3 text-center text-sm font-medium text-white"
					href="/signup"
				>
					Create Account
				</Link>
			) : null}
		</div>
	);
};
